import { type ApiProblem, type ApiSchemas, call } from '@rental/contracts/client'
import { coveringReport } from '@rental/domain'
import { format, formatDateTime, formatTime } from '@rental/i18n'
import { Button, Checkbox, Field, Notice } from '@rental/ui'
import { useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { useAccess } from '../access-context'
import { api } from '../api'
import type { CanDo } from '../booking-can'
import { ChoiceChips } from '../components/ChoiceChips'
import { mayTakeFocus, OutcomeFocus } from '../components/OutcomeNotice'
import { PendingLine } from '../components/PendingBadge'
import { Section } from '../components/Section'
import { type ChipTone, StatusChip } from '../components/StatusChip'
import { ReasonList, SubPanel } from '../components/SubPanel'
import { TechnicalLines } from '../components/TechnicalDetails'
import { FIELD, FIELD_ROW, ProblemNotice, StaffInput, StaffSelect } from '../components/ui'
import { useLocale } from '../i18n'
import { cargosSendReasons } from '../rental'
import { secondIdentityId } from './second-driver'

type Sheet = ApiSchemas['CheckoutSheet']
type Transmission = Sheet['cargos']['transmissions'][number]

/** How the contract for the car leaving stands, as the hand-over form has it. */
export type ContractState = 'digital' | 'paper' | 'none'

/**
 * Each report's state in the list, by DESIGN.md's rule: red for a refusal someone must
 * act on, orange for an outcome nobody knows (as its notice), neutral for what has
 * simply ended (a report replaced or no longer needed).
 */
const STATUS_TONE: Readonly<Record<Transmission['status'], ChipTone>> = {
  queued: 'info',
  checked: 'info',
  sent: 'success',
  recorded: 'success',
  refused: 'danger',
  unknown: 'warning',
  cancelled: 'neutral',
}

/** A queued report's sheet is read again this often, until the worker answers. */
export const CARGOS_POLL_MS = 2_000

/** Whether a sheet has a report on its way: the screen reads it again until it is answered. */
export function cargosInFlight(sheet: Sheet | undefined): boolean {
  return (
    sheet?.cargos.transmissions.some((transmission) => transmission.status === 'queued') ?? false
  )
}

/**
 * CMP-1 (decision 1): the report to CaRGOS for the car about to leave. Without
 * a receipt for this car, this version of the booking and the identity as
 * recorded, the car does not leave, whoever asks.
 *
 * - With credentials, the app sends it: the worker calls CaRGOS and the panel
 *   follows it (polled every 2 s while queued). In `check` mode the same
 *   button runs CaRGOS's dry run, which never lets a car leave.
 * - Without (PENDING), and whenever CaRGOS cannot be reached, the counter
 *   sends from CaRGOS's portal, or by PEC, and types the receipt here.
 * - An outcome nobody can know (`unknown`) is never sent again by itself:
 *   the counter checks the portal, then types its receipt or sends again.
 */
export function CargosPanel({
  sheet,
  vehicleId,
  contract,
  can,
}: {
  sheet: Sheet
  vehicleId: string
  contract: ContractState
  /** S9: the booking's own answer. A receipt typed by hand is the office's (cargosReceipt). */
  can: CanDo
}) {
  const { locale, messages: m } = useLocale()
  const { takeOver } = useAccess()
  const queryClient = useQueryClient()
  const [channel, setChannel] = useState<'portal' | 'pec'>('portal')
  const [receipt, setReceipt] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  // A receipt typed here: the report it made, from the server's answer.
  const [recordedId, setRecordedId] = useState<string | null>(null)
  // A report sent from the app: its progress takes the focus once the sheet shows that report.
  // Keyed by the report, not a flag: the server's answer and the sheet read again are drawn in
  // either order, and a flag could be spent on the report sent before, about to be replaced.
  const [sentId, setSentId] = useState<string | null>(null)
  const receiptReasonsId = useId()
  const { cargos } = sheet
  if (cargos.state === 'off') return null
  if (cargos.state === 'not_required') {
    return (
      <Section title={m.cargos.title}>
        <p>{m.cargos.notRequired}</p>
      </Section>
    )
  }

  const identity = sheet.identity.latest
  // The server decides; this mirrors its rule so the counter sees it before pressing.
  const { report, cover } = coveringReport(cargos.transmissions, {
    vehicleId,
    reservationVersion: sheet.version,
    identityId: identity?.id ?? null,
    secondIdentityId: secondIdentityId(sheet),
  })
  // Newest first: the app's latest report, when nothing typed came after it.
  const latest = cargos.transmissions[0]
  const fromApp = latest?.channel === 'api' ? latest : null
  const inFlight = fromApp?.status === 'queued'
  // The app's report is the receipt that covers: its progress and its receipt are one place.
  const appCovered = report !== null && report.id === fromApp?.id
  const recorded = recordedId !== null
  const recordedInSheet =
    recorded && cargos.transmissions.some((transmission) => transmission.id === recordedId)

  async function record() {
    if (!identity || !vehicleId || receipt.trim() === '') return
    setProblem(null)
    setRecordedId(null)
    setSentId(null)
    setBusy(true)
    const result = await call(
      api.POST('/v1/staff/bookings/{code}/cargos/receipt', {
        params: { path: { code: sheet.code } },
        body: { vehicleId, identityId: identity.id, channel, receipt: receipt.trim() },
      }),
    )
    if (result.ok) {
      setReceipt('')
      await queryClient.invalidateQueries({ queryKey: ['checkout', sheet.code] })
      setBusy(false)
      setRecordedId(result.data.transmission.id)
      return
    }
    setBusy(false)
    if (!takeOver(result.problem)) setProblem(result.problem)
  }

  const covered = report ? (
    <p>
      {format(m.cargos.covered, {
        receipt: report.receipt ?? '',
        plate: report.plate,
      })}
    </p>
  ) : null

  return (
    <Section title={m.cargos.title}>
      {/* A receipt typed: the form it was typed in goes, and what it did takes the focus.
          Drawn again once the sheet holds the receipt, so the cover it gives is read too. */}
      {recorded ? (
        <OutcomeFocus key={recordedInSheet ? `${recordedId}-in-sheet` : recordedId}>
          {covered ? <Notice tone="success">{covered}</Notice> : null}
          <p className="text-sm">{m.cargos.recorded}</p>
        </OutcomeFocus>
      ) : null}
      {!report && (cargos.transmissions.length > 0 || cover !== 'none') ? (
        <Notice tone="warning">
          <p>{m.cargos.cover[cover === 'ok' ? 'none' : cover]}</p>
        </Notice>
      ) : null}
      {/* D7: not live yet, as Sistema says it: the one badge, then what to do meanwhile. */}
      {!report && cargos.port === 'pending' ? <PendingLine>{m.cargos.pending}</PendingLine> : null}
      {/* The app's latest report, in one place from "in coda" to its receipt: the focus it
          took when sent stays on it when the receipt arrives and the forms go. */}
      {fromApp && !recorded && (appCovered || (!report && vehicleId !== '' && identity?.cargos)) ? (
        <OutcomeArea
          // A report sent again is a new one: its progress is read from the start.
          key={fromApp.id}
          transmission={fromApp}
          covered={appCovered ? covered : null}
          takeFocus={fromApp.id === sentId}
          onFocused={() => setSentId(null)}
        />
      ) : null}
      {/* Covered by a receipt typed earlier, or by an earlier report: a fact of the page. */}
      {report && !appCovered && !recorded ? <Notice tone="success">{covered}</Notice> : null}
      {report ? null : vehicleId === '' ? (
        <p>{m.cargos.needVehicle}</p>
      ) : !identity ? (
        <p>{m.cargos.needIdentity}</p>
      ) : !identity.cargos ? (
        <p>{m.cargos.needIdentityCargos}</p>
      ) : (
        <>
          {cargos.port !== 'pending' && !inFlight ? (
            <SendForm
              sheet={sheet}
              vehicleId={vehicleId}
              identityId={identity.id}
              contract={contract}
              again={fromApp !== null && fromApp.status !== 'checked'}
              afterUnknown={fromApp?.status === 'unknown'}
              onSent={(id) => {
                setRecordedId(null)
                setSentId(id)
              }}
            />
          ) : null}
          {can('cargosReceipt') ? (
            <SubPanel title={m.cargos.receiptTitle}>
              <ChoiceChips
                name={`cargos-channel-${sheet.code}`}
                label={m.cargos.channel}
                labelHidden
                options={(['portal', 'pec'] as const).map((value) => ({
                  value,
                  label: m.cargos.channels[value],
                }))}
                value={channel}
                onChange={setChannel}
              />
              <div className={FIELD_ROW}>
                <Field className={FIELD} label={m.cargos.receipt} required>
                  {(control) => (
                    <StaffInput
                      {...control}
                      autoComplete="off"
                      spellCheck={false}
                      maxLength={100}
                      value={receipt}
                      onChange={(event) => setReceipt(event.target.value)}
                    />
                  )}
                </Field>
              </div>
              <ProblemNotice problem={problem} />
              <div>
                <Button
                  variant="staff"
                  busy={busy}
                  disabled={receipt.trim() === '' && !busy}
                  aria-describedby={receipt.trim() === '' && !busy ? receiptReasonsId : undefined}
                  onClick={() => void record()}
                >
                  {m.cargos.record}
                </Button>
              </div>
              <ReasonList
                id={receiptReasonsId}
                reasons={receipt.trim() === '' && !busy ? [m.cargos.why.receipt] : []}
              />
            </SubPanel>
          ) : (
            <OfficeTypesReceipt code={sheet.code} />
          )}
        </>
      )}
      {cargos.transmissions.length > 0 ? (
        <div className="flex flex-col gap-2">
          <h3 className="text-lg">{m.cargos.history}</h3>
          <ul className="flex flex-col gap-2">
            {cargos.transmissions.map((transmission) => (
              <li key={transmission.id} className="flex flex-wrap items-center gap-2">
                <StatusChip tone={STATUS_TONE[transmission.status]}>
                  {m.cargos.status[transmission.status]}
                </StatusChip>
                <span>
                  {format(m.cargos.entry, {
                    contract: transmission.contractRef,
                    plate: transmission.plate,
                    status: m.cargos.channels[transmission.channel],
                  })}
                </span>
                {transmission.receipt ? (
                  <span className="figures">{transmission.receipt}</span>
                ) : null}
                <span className="text-sm text-text-muted">
                  {formatDateTime(locale, transmission.answeredAt ?? transmission.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Section>
  )
}

/** CaRGOS's own words quoted in a sentence of the app's: marked Italian (WCAG 3.1.2). */
function quoted(template: string, text: string): ReactNode {
  const [before = '', after = ''] = template.split('{text}')
  return (
    <>
      {before}
      <span lang="it">{text}</span>
      {after}
    </>
  )
}

/**
 * The app's latest report where its progress shows, and then its receipt.
 * Sent from here, it takes the focus once the sheet shows that report (the
 * send form is gone by then), and keeps it when the receipt arrives. Only from
 * nothing or from inside the panel: a sheet read again late never takes the
 * focus from where the counter has gone meanwhile.
 */
function OutcomeArea({
  transmission,
  covered,
  takeFocus,
  onFocused,
}: {
  transmission: Transmission
  /** The receipt this report brought, when it covers this hand-over. */
  covered: ReactNode
  takeFocus: boolean
  onFocused: () => void
}) {
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!takeFocus) return
    if (box.current && mayTakeFocus(box.current.closest('section'), { fromButtons: false })) {
      box.current.focus()
    }
    onFocused()
  }, [takeFocus, onFocused])
  return (
    <div ref={box} tabIndex={-1} className="flex flex-col gap-4 rounded-panel focus:outline-none">
      <Outcome transmission={transmission} covered={covered} />
    </div>
  )
}

/** What became of the app's latest report, in the counter's words, and what to do next. */
function Outcome({ transmission, covered }: { transmission: Transmission; covered: ReactNode }) {
  const { locale, messages: m } = useLocale()
  const { progress, errorCode, errorText } = transmission
  // The receipt came: said in the notice that said "in coda", the same live region, so the
  // change is heard where the focus already is.
  if (covered) {
    return (
      <Notice tone="success" live="polite">
        {covered}
      </Notice>
    )
  }
  switch (transmission.status) {
    case 'queued':
      if (progress?.stuck) {
        return (
          <Notice tone="danger" live="polite">
            <p>{m.cargos.stuck}</p>
          </Notice>
        )
      }
      if (progress && progress.tries > 0 && progress.nextAt) {
        return (
          <>
            <Notice tone="warning" live="polite">
              <p>
                {format(m.cargos.retrying, {
                  tries: progress.tries,
                  time: formatTime(locale, progress.nextAt),
                })}
              </p>
            </Notice>
            <TechnicalLines
              lines={
                progress.lastReason
                  ? [format(m.cargos.lastReason, { reason: progress.lastReason })]
                  : []
              }
            />
          </>
        )
      }
      return (
        <Notice tone="info" live="polite">
          <p>{m.cargos.queued}</p>
        </Notice>
      )
    case 'checked':
      return (
        <Notice tone="info" live="polite">
          <p>{m.cargos.checked}</p>
        </Notice>
      )
    case 'unknown':
      return (
        <>
          <Notice tone="warning" live="polite">
            <p>{m.cargos.unknown}</p>
          </Notice>
          <TechnicalLines lines={errorText ? [errorText] : []} />
        </>
      )
    case 'refused': {
      // CaRGOS's own description is quoted. Nothing else is: our own check of the
      // record lists our field codes, a bare HTTP answer has no text, and a body
      // with only `error` repeats its code as the text (cargos/port.ts).
      const ours = errorCode === 'record_invalid'
      const said = ours || errorText === errorCode ? null : errorText
      return (
        <>
          <Notice tone="danger" live="polite">
            <p>
              {ours
                ? m.cargos.refusedRecord
                : said
                  ? m.cargos.refused
                  : m.cargos.refusedUnexplained}
            </p>
            {said ? <p>{quoted(m.cargos.refusedSaid, said)}</p> : null}
          </Notice>
          <TechnicalLines
            lines={[
              ...(errorCode ? [format(m.cargos.technicalCode, { code: errorCode })] : []),
              ...(errorText && !said && errorText !== errorCode ? [errorText] : []),
            ]}
          />
        </>
      )
    }
    case 'cancelled': {
      const reasons: Readonly<Record<string, string>> = m.cargos.cancelled
      // Ended, as its chip says; orange only where the counter must send again.
      const again = errorCode === 'booking_changed' || errorCode === 'identity_changed'
      return (
        <Notice tone={again ? 'warning' : 'info'} live="polite">
          <p>{(errorCode ? reasons[errorCode] : undefined) ?? m.cargos.cancelled.default}</p>
        </Notice>
      )
    }
    default:
      // Sent, and its receipt does not cover this hand-over (another car, the booking or the
      // details changed since): the cover warning above says why.
      return null
  }
}

/**
 * The report from the app: CONTRATTO_TIPOP from CaRGOS's table (cash and a
 * bank transfer are suggested; a card is the counter's choice, Q-109), and a
 * contract already signed, on the iPad or on paper. In `check` mode the same
 * button runs CaRGOS's dry run.
 */
function SendForm({
  sheet,
  vehicleId,
  identityId,
  contract,
  again,
  afterUnknown,
  onSent,
}: {
  sheet: Sheet
  vehicleId: string
  identityId: string
  contract: ContractState
  again: boolean
  /** The last report may be at CaRGOS: sent again only once the counter has looked. */
  afterUnknown: boolean
  /** Sent, and the sheet read again: the report on its way, by its id. */
  onSent: (transmissionId: string) => void
}) {
  const { messages: m } = useLocale()
  const { takeOver } = useAccess()
  const queryClient = useQueryClient()
  const { cargos } = sheet
  const [paymentType, setPaymentType] = useState(cargos.suggestedPaymentType ?? '')
  const [portalChecked, setPortalChecked] = useState(false)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const trial = cargos.port === 'check'
  const reasons = cargosSendReasons({ paymentType, contract, afterUnknown, portalChecked })
  const ready = reasons.length === 0
  const reasonsId = useId()

  async function send() {
    if (!ready) return
    setProblem(null)
    setBusy(true)
    const result = await call(
      api.POST('/v1/staff/bookings/{code}/cargos/send', {
        params: { path: { code: sheet.code } },
        body: {
          vehicleId,
          identityId,
          paymentType,
          ...(contract === 'paper' ? { paperContract: true as const } : {}),
          ...(afterUnknown && portalChecked ? { portalChecked: true as const } : {}),
        },
      }),
    )
    setBusy(false)
    if (result.ok) {
      await queryClient.invalidateQueries({ queryKey: ['checkout', sheet.code] })
      onSent(result.data.transmission.id)
      return
    }
    if (!takeOver(result.problem)) setProblem(result.problem)
  }

  return (
    <SubPanel title={m.cargos.sendTitle}>
      {trial ? (
        <Notice tone="info">
          <p>{m.cargos.checkMode}</p>
        </Notice>
      ) : null}
      <div className={FIELD_ROW}>
        <Field className={FIELD} label={m.cargos.paymentType} required>
          {(control) => (
            <StaffSelect
              {...control}
              value={paymentType}
              onChange={(event) => setPaymentType(event.target.value)}
            >
              <option value="">{m.cargos.choosePaymentType}</option>
              {cargos.paymentTypes.map((type) => (
                // CaRGOS's table is in Italian, whatever the app's language (WCAG 3.1.2).
                <option key={type.code} value={type.code} lang="it">
                  {type.code} · {type.description}
                </option>
              ))}
            </StaffSelect>
          )}
        </Field>
      </div>
      {afterUnknown ? (
        <Checkbox
          label={m.cargos.portalChecked}
          checked={portalChecked}
          onChange={(event) => setPortalChecked(event.target.checked)}
        />
      ) : null}
      <ProblemNotice problem={problem} />
      <div>
        <Button
          variant="staff"
          busy={busy}
          disabled={!ready && !busy}
          aria-describedby={!ready && !busy ? reasonsId : undefined}
          onClick={() => void send()}
        >
          {trial ? m.cargos.check : again ? m.cargos.sendAgain : m.cargos.send}
        </Button>
      </div>
      <ReasonList
        id={reasonsId}
        reasons={busy ? [] : reasons.map((reason) => m.cargos.why[reason])}
      />
    </SubPanel>
  )
}

/** How often a trip waiting on the office's receipt reads the sheet again: a quay's signal is thin. */
const OFFICE_RECEIPT_POLL_MS = 15_000

/**
 * S9 (Q-62): on a trip the receipt from CaRGOS's portal or a PEC is the
 * office's to type. The hand-over waits for it, and reads the sheet again
 * until it is there.
 */
function OfficeTypesReceipt({ code }: { code: string }) {
  const { messages: m } = useLocale()
  const queryClient = useQueryClient()
  useEffect(() => {
    const timer = window.setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: ['checkout', code] })
    }, OFFICE_RECEIPT_POLL_MS)
    return () => window.clearInterval(timer)
  }, [queryClient, code])
  return <p>{m.cargos.officeReceipt}</p>
}
